// A job's result, right under the form that started it (the Business OS way):
// Sent → Working → the agent's answer, any approval with Approve / Reject, and
// a Revise box. The work runs in the agent's own room behind the scenes; the
// owner never has to open a chat.
import { type ApprovalItem, type ChatMessage, fetchRoom, sendMessage } from '@/lib/ai-team'
import { ApiError } from '@/lib/api'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { AgentAvatar } from '@/pages/ai-team/AgentAvatar'
import { CHIP, type Tone, card } from '@/pages/ai-team/hubs'
import { timeAgo } from '@/pages/ai-team/workflows/shared'
import { useQuery } from '@tanstack/react-query'
import { Check, Loader2, RefreshCw, Send, X } from 'lucide-react'
import { type FormEvent, useCallback, useState } from 'react'
import { DecisionCard, field, ghostBtn, goBtn } from './parts'

export interface ResultRef {
  /** The agent's room the work runs in. */
  roomId: number
  /** Our message that started it: only what comes after belongs to this result. */
  afterId: number
  /**
   * Every message this card sent (the first is afterId, then each Revise). The
   * room is shared, so another admin message not in here ends this result.
   * Missing on refs stored before it existed.
   */
  ids?: number[]
  title: string
  agentKey?: string
  agentName: string
  at: string
}

const KEEP = 5

const sameRef = (a: ResultRef, b: ResultRef) => a.roomId === b.roomId && a.afterId === b.afterId

function load(key: string): ResultRef[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) || '[]')
    return Array.isArray(parsed)
      ? (parsed as ResultRef[]).filter((r) => r && typeof r.roomId === 'number' && typeof r.afterId === 'number')
      : []
  } catch {
    return []
  }
}

/** The last few results for one place (a team, the Overview), kept across reloads. */
export function useResults(scope: string) {
  const key = `ai-results:${scope}`
  const [state, setState] = useState(() => ({ key, items: load(key) }))
  // Another place (e.g. switching teams): read its own results.
  const current = state.key === key ? state : { key, items: load(key) }
  if (current !== state) setState(current)
  const items = current.items
  const save = useCallback(
    (next: ResultRef[]) => {
      setState({ key, items: next })
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        // Private mode: results just won't survive a reload.
      }
    },
    [key]
  )
  return {
    items,
    add: (r: ResultRef) =>
      save([{ ...r, ids: r.ids ?? [r.afterId] }, ...items.filter((i) => !sameRef(i, r))].slice(0, KEEP)),
    remove: (r: ResultRef) => save(items.filter((i) => !sameRef(i, r))),
    /** Remember a message the card sent (Revise), so its reply stays on the card. */
    addSent: (r: ResultRef, id: number) =>
      save(items.map((i) => (sameRef(i, r) ? { ...i, ids: [...(i.ids ?? [i.afterId]), id] } : i)))
  }
}

export function ResultList({
  items,
  onDismiss,
  onSent
}: {
  items: ResultRef[]
  onDismiss: (r: ResultRef) => void
  onSent?: (r: ResultRef, id: number) => void
}) {
  if (items.length === 0) return null
  return (
    <div className="space-y-3">
      {items.map((r) => (
        <ResultCard
          key={`${r.roomId}:${r.afterId}`}
          result={r}
          onDismiss={() => onDismiss(r)}
          onSent={(id) => onSent?.(r, id)}
        />
      ))}
    </div>
  )
}

/**
 * This card's part of a shared room: after its first message, up to someone
 * else's request (and again from this card's next Revise, if any).
 * `cut` = the room has moved on to someone else's request.
 */
function sliceFor(messages: ChatMessage[], result: ResultRef) {
  const ids = result.ids?.length ? result.ids : [result.afterId]
  const out: ChatMessage[] = []
  let cut = false
  for (const m of messages) {
    if (m.id <= ids[0]) continue
    if (m.role === 'admin') cut = !ids.includes(m.id)
    if (!cut) out.push(m)
  }
  const last = ids[ids.length - 1]
  // Answered since the latest thing this card asked (a Revise waits for its own reply).
  const answered = out.some((m) => m.id > last && m.role === 'agent' && m.text.trim())
  return { mine: out, cut, answered }
}

type Step = { text: string; tone: Tone; spin?: boolean }

function ResultCard({
  result,
  onDismiss,
  onSent
}: {
  result: ResultRef
  onDismiss: () => void
  onSent: (id: number) => void
}) {
  const room = useQuery({
    queryKey: ['ai-room', result.roomId],
    queryFn: () => fetchRoom(result.roomId),
    refetchInterval: (q) => {
      if (q.state.status === 'error') return false
      const data = q.state.data
      if (!data) return 10_000
      const { mine, cut, answered } = sliceFor(data.messages ?? [], result)
      const isWorking = data.working && !cut
      const hasOpen = mine.some((m) => m.card?.status === 'open')
      if (!isWorking && !hasOpen && answered) return false
      return isWorking ? 2_000 : 10_000
    }
  })
  const [revise, setRevise] = useState('')
  const [sending, setSending] = useState(false)
  const gone = room.error instanceof ApiError && room.error.status === 404

  const { mine, cut, answered } = sliceFor(room.data?.messages ?? [], result)
  // The room may be busy with someone else's request; that isn't this card working.
  const working = !!room.data?.working && !cut
  const answers = mine.filter((m) => (m.role === 'agent' || m.role === 'admin') && m.text.trim())
  const cards = mine.filter((m) => m.card).map((m) => m.card!)
  const open = cards.filter((c) => c.status === 'open')
  const failed = mine.some((m) => m.role === 'step' && m.data?.status === 'error') && !working
  const replied = mine.some((m) => m.role === 'agent' && m.text.trim())

  const step: Step = gone
    ? { text: 'Deleted', tone: 'neutral' }
    : room.isLoading
    ? { text: 'Sent', tone: 'neutral', spin: true }
    : working
      ? { text: 'Working… usually under a minute', tone: 'emerald', spin: true }
      : open.length > 0
        ? { text: 'Waiting for your yes', tone: 'amber' }
        : failed && !answered
          ? { text: 'Hit a problem', tone: 'red' }
          : answered
            ? { text: 'Done', tone: 'emerald' }
            : { text: 'Queued', tone: 'neutral', spin: true }

  async function send(e: FormEvent) {
    e.preventDefault()
    if (!revise.trim() || sending || gone) return
    setSending(true)
    try {
      const sent = await sendMessage(result.roomId, revise.trim())
      onSent(sent.id)
      setRevise('')
      await queryClient.invalidateQueries({ queryKey: ['ai-room', result.roomId] })
    } catch (err) {
      notifyError('Could not send that', err)
    } finally {
      setSending(false)
    }
  }

  return (
    <article className={cn(card, 'space-y-4')}>
      <header className="flex items-start gap-3">
        <AgentAvatar agentKey={result.agentKey} name={result.agentName} size="md" state={working ? 'working' : 'idle'} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="font-bold leading-snug text-neutral-900 [overflow-wrap:anywhere] dark:text-white">{result.title}</p>
          <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
            {result.agentName} · started {timeAgo(result.at)}
          </p>
          <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold', CHIP[step.tone])}>
            {step.spin ? <Loader2 className="size-3 animate-spin" /> : step.tone === 'emerald' ? <Check className="size-3" /> : null}
            {step.text}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
        >
          <X className="size-4" />
        </button>
      </header>

      {gone ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          This conversation was deleted.
          <button type="button" onClick={onDismiss} className={cn(ghostBtn, 'px-4 py-1.5')}>
            Dismiss
          </button>
        </div>
      ) : (
        room.error && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-red-600 dark:text-red-400">
            Couldn't load the result. It's still in the work log.
            <button type="button" onClick={() => room.refetch()} className={cn(ghostBtn, 'px-4 py-1.5')}>
              Try again
            </button>
          </div>
        )
      )}

      {answers.length > 0 && (
        <div className="space-y-2">
          {answers.map((m) => (
            <Answer key={m.id} message={m} />
          ))}
        </div>
      )}

      {cards.map((c) => {
        const msg = mine.find((m) => m.card?.id === c.id)!
        if (c.status !== 'open')
          return (
            <p key={c.id} className="text-sm text-neutral-500 dark:text-neutral-400">
              {c.title}: <span className="font-semibold">{c.status_label}</span>
            </p>
          )
        const item: ApprovalItem = {
          ...c,
          agent: msg.agent ?? { id: 0, key: result.agentKey ?? '', name: result.agentName },
          created_at: msg.created_at,
          room: null,
          source: ''
        }
        return (
          <div key={c.id} className="space-y-2">
            {c.draft && (
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-[24px] border border-neutral-200 bg-neutral-50 p-4 font-sans text-sm text-neutral-800 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
                {c.draft}
              </pre>
            )}
            <DecisionCard entry={{ key: `c:${c.id}`, kind: 'one', item }} />
          </div>
        )
      })}

      {!room.isLoading && !gone && (
        <form onSubmit={send} className="flex flex-col gap-2 sm:flex-row">
          <label className="sr-only" htmlFor={`revise-${result.roomId}-${result.afterId}`}>
            Ask for a change
          </label>
          <input
            id={`revise-${result.roomId}-${result.afterId}`}
            value={revise}
            onChange={(e) => setRevise(e.target.value)}
            placeholder="Ask for a change, e.g. “make it shorter” or “only Atlanta”"
            className={field}
          />
          <button type="submit" disabled={sending || !revise.trim()} className={cn(goBtn, 'shrink-0')}>
            {sending ? <Loader2 className="size-4 animate-spin" /> : replied ? <RefreshCw className="size-4" /> : <Send className="size-4" />}
            {replied ? 'Revise' : 'Send'}
          </button>
        </form>
      )}
    </article>
  )
}

function Answer({ message: m }: { message: ChatMessage }) {
  if (m.role === 'admin')
    return (
      <p className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-[24px] bg-emerald-500/10 px-4 py-2 text-sm text-emerald-800 dark:text-emerald-200">
        {m.text}
      </p>
    )
  return (
    <div className="whitespace-pre-wrap rounded-[24px] bg-neutral-50 px-4 py-3 text-sm leading-6 text-neutral-800 dark:bg-white/5 dark:text-neutral-100">
      {m.text}
    </div>
  )
}
